import React, { FC, useEffect, useState } from 'react'
import { APIDecoratorWithBaseURI } from '../../Service/api'
import {
  CfgAuto,
  CfgBadge,
  CfgIdx,
  CfgInput,
  CfgName,
  CfgNote,
  CfgRow,
  CfgRows,
  CfgUnit,
  ModalBtn,
  ModalClose,
  ModalFoot,
  ModalHead,
  ModalOverlay,
  ModalPanel,
  ModalSub,
  ModalTitle,
} from './styles'

const PA_PREFIX = 'producer/analytics/'

interface PeriodCfg {
  name: string
  color: string
  days: number | null
}

interface Props {
  eventId: string
  onClose: () => void
  onSaved: () => void
}

const SalesCurveConfigModal: FC<Props> = ({ eventId, onClose, onSaved }) => {
  const [periods, setPeriods] = useState<PeriodCfg[]>([])
  const [totalDias, setTotalDias] = useState<number>(0)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let active = true
    APIDecoratorWithBaseURI()
      .get<any>(`${PA_PREFIX}salesCurveConfig?eventId=${eventId}`)
      .then((r) => {
        if (!active) return
        const body = r?.data?.body
        if (body?.periods) {
          setPeriods(body.periods)
          setTotalDias(Number(body.totalDias) || 0)
        }
      })
      .catch(() => undefined)
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [eventId])

  const setDays = (idx: number, value: string) => {
    const v = Math.max(parseInt(value, 10) || 0, 0)
    setPeriods((prev) => prev.map((p, i) => (i === idx ? { ...p, days: v } : p)))
  }

  const somaFixas = periods
    .slice(0, -1)
    .reduce((s, p) => s + (Number(p.days) || 0), 0)
  const restante = Math.max(totalDias - somaFixas, 0)
  const invalido = totalDias > 0 && somaFixas >= totalDias

  const dividirTercos = () => {
    const n = periods.length
    if (!n) return
    const base = Math.floor(totalDias / n)
    setPeriods((prev) => prev.map((p, i) => ({ ...p, days: i === n - 1 ? null : base })))
  }

  const salvar = async () => {
    if (invalido) return
    setSaving(true)
    try {
      await APIDecoratorWithBaseURI().put(`${PA_PREFIX}salesCurveConfig?eventId=${eventId}`, {
        periods: periods.map((p, i) => ({
          name: p.name,
          color: p.color,
          days: i === periods.length - 1 ? null : Number(p.days) || 1,
        })),
      })
      onSaved()
      onClose()
    } catch {
      setSaving(false)
    }
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalPanel onClick={(e) => e.stopPropagation()}>
        <ModalHead>
          <ModalTitle>Configurar períodos</ModalTitle>
          {totalDias > 0 && <CfgBadge>{totalDias} dias de venda</CfgBadge>}
          <ModalClose type="button" onClick={onClose} aria-label="Fechar">
            ×
          </ModalClose>
        </ModalHead>
        <ModalSub>
          Defina a duração das primeiras faixas em dias. A última é sempre o restante,
          calculado automaticamente.
        </ModalSub>

        {loading ? (
          <CfgNote>Carregando configuração…</CfgNote>
        ) : (
          <>
            <CfgRows>
              {periods.map((p, i) => {
                const isLast = i === periods.length - 1
                return (
                  <CfgRow key={`${p.name}-${i}`}>
                    <CfgIdx $color={p.color}>{i + 1}</CfgIdx>
                    <CfgName>{p.name}</CfgName>
                    {isLast ? (
                      <CfgAuto>Restante · {restante} dias</CfgAuto>
                    ) : (
                      <>
                        <CfgInput
                          value={String(p.days ?? 0)}
                          inputMode="numeric"
                          onChange={(e) => setDays(i, e.target.value)}
                        />
                        <CfgUnit>dias</CfgUnit>
                      </>
                    )}
                  </CfgRow>
                )
              })}
            </CfgRows>

            <CfgNote $error={invalido}>
              {invalido
                ? `A soma das faixas fixas (${somaFixas}) precisa ser menor que ${totalDias} dias.`
                : `${somaFixas} + ${restante} = ${totalDias} dias. Recalcula sozinho se a 1ª venda ou a data do evento mudar.`}
            </CfgNote>

            <ModalFoot>
              <ModalBtn type="button" $variant="ghost" onClick={dividirTercos}>
                Dividir em partes iguais
              </ModalBtn>
              <ModalBtn type="button" onClick={salvar} disabled={invalido || saving}>
                {saving ? 'Salvando…' : 'Salvar configuração'}
              </ModalBtn>
            </ModalFoot>
          </>
        )}
      </ModalPanel>
    </ModalOverlay>
  )
}

export default SalesCurveConfigModal
